import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { FormikTouched, FormikErrors } from 'formik';
import CivilStatusList from 'shell/components/shared/civil-status-list';
import { AccordionCivilStatus } from 'shell/components/shared/sipo-person-form/new-person';
import { CivilStatusID } from 'shell/constants/civil-status';

interface FormCivilStatusProps {
  errors?: FormikErrors<AccordionCivilStatus>;
  touched?: FormikTouched<AccordionCivilStatus>;
  values?: AccordionCivilStatus;
  onChange: (
    key: keyof AccordionCivilStatus,
    value: AccordionCivilStatus[keyof AccordionCivilStatus]
  ) => void;
  onChanges: (values: AccordionCivilStatus) => void;
  setHasChangedCivilStatus: (hasChanged: boolean) => void;
}

const FormCivilStatusComponent = ({
  values,
  onChange,
  onChanges,
  errors,
  touched,
  setHasChangedCivilStatus,
}: FormCivilStatusProps): React.ReactElement => {
  const handleCivilStatusListChange = useEventCallback((element: string | undefined) => {
    onChange('status', element);
    setHasChangedCivilStatus(true);
  });

  const onChangeCheckbox = useEventCallback(() => {
    onChanges({
      available: !values?.available,
      status: !values?.available ? undefined : values.status,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item container spacing={2} xs={12} alignItems="center">
        <Grid item xs={12} md={6}>
          <CivilStatusList
            required
            label="Stato civile"
            aria-label="Stato civile"
            value={values?.status || ''}
            onChange={handleCivilStatusListChange}
            error={touched?.status && Boolean(errors?.status)}
            disabled={values?.available}
          />
        </Grid>
        <Grid item xs>
          <FormControlLabel
            control={
              <Checkbox
                id="availableStatus"
                checked={values?.available || false}
                onChange={() => onChangeCheckbox()}
                inputProps={{ 'aria-label': 'Stato civile non disponibile' }}
                disabled
              />
            }
            disabled
            label="Stato civile non disponibile"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Note"
          aria-label="Note"
          id="note"
          fullWidth
          multiline
          rows={5}
          inputProps={{
            maxLength: '240',
          }}
          value={values?.note || ''}
          disabled={values?.status?.toString() !== CivilStatusID.nonClassificabile}
          onChange={(event) => {
            onChange('note', event?.target.value);
          }}
          error={touched?.note && Boolean(errors?.note)}
          helperText={errors?.note}
        />
      </Grid>
    </Grid>
  );
};

FormCivilStatusComponent.displayName = 'Form civil status component';

const FormCivilStatus = memo(FormCivilStatusComponent);

export default FormCivilStatus;
